<script setup lang="ts">
import useModel from '../model/model'
import { computed, ref, watch, watchEffect } from 'vue'
import { useQueries } from '@tanstack/vue-query'
import {
  formatStatusTriggersDisplay,
  formatSubscriptionScheduleDisplay,
  parseObjectTypeDetailResponse,
  useHttp,
  useTranslate,
} from '@/shared/lib'
import { ObjectSearch } from '@/widgets'
import { useLoadRoleObjectTypes, useSendForm } from './../model/api'
import { ObjectTypeIds } from '@/entities/Object'
import { useAuth } from '@/entities/Auth'
import { AcceptStatus } from '@/shared/ui/VInventAccentStatus/model/types'
import { createSubscriptionForm } from '../model/subscriptionForm'
import type { RoleStatusSubscription } from '@/entities/Role'
import { ObjectTypePermissionsModal } from '@/features/Role/ObjectTypePermissionsModal'
import { ElementPermissionsModal } from '@/features/Role/ElementPermissionsModal'
import { PassportDetailedViewPermissionsModal } from '@/features/Role/PassportDetailedViewPermissionsModal'
import { useRoute, useRouter } from 'vue-router'
import { VIcons } from '@/shared/ui'

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()
const roleId = computed<number>(() => Number(route.params.entity_id))

const model = useModel()
const auth = useAuth()
const { trs } = useTranslate()
const { isPending: isSaved, sendForm } = useSendForm()
const { isLoading, refetch } = model.loadRole(roleId.value)

const valid = ref(false)
const showTypePermissionsModal = ref(false)
const showElementPermissionsModal = ref(false)
const showPassportDetailedViewPermissionsModal = ref(false)

const {
  form: subscriptionForm,
  editIndex: editSubscriptionIndex,
  localError,
  reset: resetSubscriptionForm,
  save: saveToList,
  startEdit: startEditSubscription,
  remove: removeFromList,
} = createSubscriptionForm()

const statusOrder = ['DRAFT', 'PREACCEPT', 'REWORK', 'ACCEPTED', 'CANCELLED']

const statusOptions = computed(() =>
  statusOrder.map((status) => ({
    title: AcceptStatus[status as keyof typeof AcceptStatus]?.name || status,
    value: status,
  })),
)

const extractRows = (raw: unknown): Record<string, any>[] => {
  if (Array.isArray(raw)) {
    return raw.filter(
      (item): item is Record<string, any> => Boolean(item) && typeof item === 'object',
    )
  }
  if (!raw || typeof raw !== 'object') return []

  const candidate = raw as Record<string, unknown>
  if (Array.isArray(candidate.data)) return extractRows(candidate.data)
  if (Array.isArray(candidate.object_types)) return candidate.object_types as any[]
  if (Array.isArray(candidate.subscriptions)) return candidate.subscriptions as any[]
  if (Array.isArray(candidate.items)) return candidate.items as any[]
  if (Array.isArray(candidate.rows)) return candidate.rows as any[]

  return []
}

const roleGroupId = computed(
  () =>
    Number(model.data.group_id) ||
    Number(route.params.object_id) ||
    Number(auth.user?.object_group?.id) ||
    0,
)
const objectTypesQuery = useLoadRoleObjectTypes(roleGroupId)
const isObjectTypesLoading = computed(() => objectTypesQuery.isLoading.value)
const http = useHttp()

const objectTypesListRoot = computed(() => {
  const raw = objectTypesQuery.data.value as
    | { data?: unknown; payload?: unknown; payloads?: unknown }
    | null
    | undefined
  if (!raw) return undefined
  return raw.data ?? raw.payload ?? raw.payloads
})

const objectTypeRows = computed(() => extractRows(objectTypesListRoot.value))

const objectTypeOptions = computed(() => {
  return objectTypeRows.value
    .map((item) => ({
      title: item.name || item.object_name || String(item.id),
      value: Number(item.id),
    }))
    .filter((item) => Number.isFinite(item.value))
})

const objectTypeNameByIdFromList = computed(() => {
  const map = new Map<number, string>()
  for (const item of objectTypeRows.value) {
    const title = (item.name || item.object_name || String(item.id)) as string
    const catalogId = Number(item.id)
    const extTypeId = Number(
      item.object_type_id ?? (item.object_type as Record<string, unknown> | undefined)?.id,
    )
    if (Number.isFinite(catalogId) && catalogId > 0) map.set(catalogId, title)
    if (Number.isFinite(extTypeId) && extTypeId > 0 && extTypeId !== catalogId)
      map.set(extTypeId, title)
  }
  return map
})

const subscriptionObjectTypeIds = computed(() => {
  const want = new Set<number>()
  const defaults = model.data.status_subscription_defaults
  if (Array.isArray(defaults)) {
    for (const row of defaults) {
      const id = Number(row.object_type_id)
      if (Number.isFinite(id) && id > 0) want.add(id)
    }
  }
  const fid = Number(subscriptionForm.object_type_id)
  if (Number.isFinite(fid) && fid > 0) want.add(fid)
  return [...want].sort((a, b) => a - b)
})

const objectTypeIdsNeedingDetailFetch = computed(() => {
  const base = objectTypeNameByIdFromList.value
  return subscriptionObjectTypeIds.value.filter((id) => !base.has(id))
})

const roleObjectTypeDetailQueries = useQueries({
  queries: computed(() => {
    const gid = roleGroupId.value
    return objectTypeIdsNeedingDetailFetch.value.map((id) => ({
      queryKey: ['role_status_subscription_object_type_detail', gid, id] as const,
      queryFn: async ({ queryKey }) => {
        const [, g, typeId] = queryKey as readonly [string, number, number]
        const res = await http.get(`/inventory/group/${g}/object-types/${typeId}`)
        const parsed = parseObjectTypeDetailResponse(res)
        if (!parsed?.title) throw new Error(`object type ${typeId} not found`)
        return { requestedId: typeId, title: parsed.title }
      },
      enabled: gid > 0 && id > 0,
      staleTime: 60 * 60 * 1000,
    }))
  }),
})

const objectTypeNameById = computed(() => {
  const map = new Map(objectTypeNameByIdFromList.value)
  for (const q of roleObjectTypeDetailQueries.value) {
    const row = q.data as { requestedId?: number; title?: string } | undefined
    if (row?.requestedId && row.title) map.set(row.requestedId, row.title)
  }
  return map
})

const saveStatusSubscription = () => {
  const selectedTypeID = Number(subscriptionForm.object_type_id)
  const hasTypeOptions = objectTypeOptions.value.length > 0
  if (hasTypeOptions && (!selectedTypeID || !objectTypeNameById.value.has(selectedTypeID))) {
    localError.value = 'Выберите тип элемента из текущей группы'
    return
  }

  if (!Array.isArray(model.data.status_subscription_defaults)) {
    model.data.status_subscription_defaults = []
  }
  saveToList(model.data.status_subscription_defaults)
}

const removeStatusSubscription = (index: number) => {
  if (!Array.isArray(model.data.status_subscription_defaults)) return
  removeFromList(model.data.status_subscription_defaults, index)
}

const setStatusSubscriptionActive = (item: RoleStatusSubscription, value: boolean | null) => {
  item.is_active = Boolean(value)
}

watch(
  () => model.data.status_subscription_defaults,
  (value) => {
    if (!Array.isArray(value)) {
      model.data.status_subscription_defaults = []
    }
  },
  { immediate: true },
)

/** Следим за ид пользователя и подгружаем данные */
watchEffect(() => {
  if (roleId.value) refetch()
})

/** Ссылка на возврат к списку */
const router = useRouter()

const backLink = computed(() => {
  return router.resolve({
    name: 'objects.groups.admin',
    params: {
      ...route.params,
      section: 'roles',
      action: null,
      entity_id: null,
    },
  })
})

const roleTitle = computed(() => {
  return model.data.name || (roleId.value ? trs('itf.role_edit') : trs('itf.role_create'))
})

const roleSubtitle = computed(() => {
  const parts: string[] = []
  if (roleId.value) parts.push(`Ид.: ${roleId.value}`)
  if (model.data.group_id) parts.push(`${trs('itf.objects_group')}: ${model.data.group_id}`)
  return parts.join(' \u00B7 ')
})
</script>

<template>
  <VForm
    v-model="valid"
    class="role-edit-v2"
    @submit.prevent="sendForm(model.data, valid)"
    :disabled="disabled || isLoading"
  >
    <!-- ===== HERO HEADER ===== -->
    <header class="role-edit-v2__hero">
      <VBtn
        icon
        variant="text"
        density="comfortable"
        class="role-edit-v2__back-btn"
        :aria-label="$t('itf.back_list')"
        :to="backLink"
      >
        <VIcons type="arrow_back" />
      </VBtn>

      <div class="role-edit-v2__hero-copy">
        <div class="role-edit-v2__title-row">
          <h1 class="role-edit-v2__title">
            {{ roleTitle }}
          </h1>
        </div>
        <p class="role-edit-v2__subtitle" v-if="roleSubtitle">
          {{ roleSubtitle }}
        </p>
      </div>

      <div class="role-edit-v2__actions">
        <div class="role-edit-v2__controls">
          <VBtn :to="backLink" color="default" variant="outlined">
            <VIcons type="arrow_back" />
            {{ $t('itf.back_list') }}
          </VBtn>

          <VBtn type="submit" :loading="isSaved" :disabled="disabled">
            <VIcons type="save" />
            {{ $t('itf.save') }}
          </VBtn>
        </div>
      </div>
    </header>

    <!-- ===== BODY ===== -->
    <div class="role-edit-v2__body">
      <!-- Basic Info -->
      <section class="role-edit-v2__card">
        <div class="role-edit-v2__card-header">
          <VIcons type="info" color="primary" />
          <h2 class="role-edit-v2__card-title">{{ $t('itf.base_information') }}</h2>
        </div>
        <div class="role-edit-v2__card-body">
          <div class="role-edit-v2__form-grid">
            <VTextField
              :label="$t('itf.name')"
              v-model="model.data.name"
              :rules="model.rules.name"
            />

            <ObjectSearch
              :label="$t('itf.objects_group')"
              v-model="model.data.group_id"
              :object_type="ObjectTypeIds.GROUPS"
            />
          </div>
        </div>
      </section>

      <!-- Permissions -->
      <section class="role-edit-v2__card">
        <div class="role-edit-v2__card-header">
          <VIcons type="shield" color="primary" />
          <h2 class="role-edit-v2__card-title">{{ $t('itf.permissions') }}</h2>
        </div>
        <div class="role-edit-v2__card-body">
          <div class="role-edit-v2__checkbox-grid">
            <VCheckbox
              v-for="permission in model.listPermissions"
              v-model="model.data.permissions"
              :key="permission.code"
              :value="permission.code"
              density="compact"
              hide-details
            >
              <template #label>
                <VTooltip :text="permission.description" location="top" :open-delay="500">
                  <template #activator="{ props: tooltipProps }">
                    <span v-bind="tooltipProps">{{ permission.name }}</span>
                  </template>
                </VTooltip>
              </template>
            </VCheckbox>
          </div>
        </div>
      </section>

      <!-- Tabs -->
      <section class="role-edit-v2__card">
        <div class="role-edit-v2__card-header">
          <VIcons type="tab" color="primary" />
          <h2 class="role-edit-v2__card-title">{{ $t('itf.tabs_preset') }}</h2>
        </div>
        <div class="role-edit-v2__card-body">
          <div class="role-edit-v2__checkbox-grid">
            <VCheckbox
              v-for="tab in model.listTabs"
              v-model="model.data.tabs_preset"
              :key="tab.code"
              :value="tab.code"
              density="compact"
              hide-details
            >
              <template #label>
                <span>{{ tab.name }}</span>
              </template>
            </VCheckbox>
          </div>
        </div>
      </section>

      <!-- Map Layers -->
      <section class="role-edit-v2__card">
        <div class="role-edit-v2__card-header">
          <VIcons type="map" color="primary" />
          <h2 class="role-edit-v2__card-title">{{ $t('itf.map_layers_permission') }}</h2>
        </div>
        <div class="role-edit-v2__card-body">
          <div class="role-edit-v2__checkbox-grid">
            <VCheckbox
              v-for="layer in model.listLayers"
              v-model="model.data.map_layers_permissions"
              :key="layer.permission"
              :value="layer.permission"
              density="compact"
              hide-details
            >
              <template #label>
                <span>{{ $t(`itf.${layer.label}`) }}</span>
              </template>
            </VCheckbox>
          </div>
        </div>
      </section>

      <!-- Notifications -->
      <section class="role-edit-v2__card">
        <div class="role-edit-v2__card-header">
          <VIcons type="notifications" color="primary" />
          <h2 class="role-edit-v2__card-title">{{ $t('itf.notifications_permissions') }}</h2>
        </div>
        <div class="role-edit-v2__card-body">
          <div class="role-edit-v2__checkbox-grid">
            <VCheckbox
              v-for="notify in model.listNotifications"
              v-model="model.data.notification_preset"
              :key="notify.code"
              :value="notify.code"
              density="compact"
              hide-details
            >
              <template #label>
                <span>{{ notify.name }}</span>
              </template>
            </VCheckbox>
          </div>
        </div>
      </section>

      <!-- Status Subscriptions -->
      <section class="role-edit-v2__card">
        <div class="role-edit-v2__card-header">
          <VIcons type="mark_email_unread" color="primary" />
          <h2 class="role-edit-v2__card-title">{{ $t('itf.subscribes') }}</h2>
        </div>
        <div class="role-edit-v2__card-body">
          <div class="role-edit-v2__subform">
            <VAutocomplete
              v-model="subscriptionForm.object_type_id"
              :items="objectTypeOptions"
              :label="$t('itf.object_type')"
              item-title="title"
              item-value="value"
              :loading="isObjectTypesLoading"
              clearable
              auto-select-first
              hide-no-data
              no-data-text=""
              autocomplete="off"
              menu-icon=""
              :menu-props="{ maxHeight: 320 }"
              class="role-edit-v2__subform-field"
            />

            <VSelect
              v-model="subscriptionForm.status_triggers"
              :items="statusOptions"
              :label="$t('itf.status')"
              multiple
              chips
              closable-chips
              item-title="title"
              item-value="value"
              class="role-edit-v2__subform-field"
            />

            <VCheckbox
              v-model="subscriptionForm.is_active"
              :label="$t('itf.active')"
              hide-details
              density="compact"
              class="role-edit-v2__subform-switch"
            />

            <div class="role-edit-v2__subform-actions">
              <VBtn type="button" color="default" variant="tonal" @click="saveStatusSubscription">
                <VIcons type="add" />
                {{ $t('itf.save') }}
              </VBtn>
              <VBtn type="button" color="default" variant="text" @click="resetSubscriptionForm">
                {{ $t('itf.clear') }}
              </VBtn>
            </div>
          </div>

          <VAlert v-if="localError" type="error" variant="tonal" class="mb-4">
            {{ localError }}
          </VAlert>

          <VTable v-if="model.data.status_subscription_defaults?.length" density="comfortable">
            <thead>
              <tr>
                <th>{{ $t('itf.object_type') }}</th>
                <th>{{ $t('itf.status') }}</th>
                <th>{{ $t('itf.schedule') }}</th>
                <th>{{ $t('itf.active') }}</th>
                <th style="width: 160px">{{ $t('itf.action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in model.data.status_subscription_defaults"
                :key="`${item.object_type_id}-${index}`"
              >
                <td>
                  {{ objectTypeNameById.get(item.object_type_id) || `#${item.object_type_id}` }}
                </td>
                <td>{{ formatStatusTriggersDisplay(item.status_triggers) }}</td>
                <td>{{ formatSubscriptionScheduleDisplay(item.schedule) }}</td>
                <td>
                  <VCheckbox
                    :model-value="item.is_active"
                    hide-details
                    density="compact"
                    :disabled="disabled || isLoading || isSaved"
                    @update:model-value="setStatusSubscriptionActive(item, $event)"
                  />
                </td>
                <td>
                  <div class="d-flex ga-2">
                    <VBtn
                      type="button"
                      size="small"
                      variant="tonal"
                      @click.stop="startEditSubscription(item, index)"
                    >
                      <VIcons type="edit" />
                      {{ $t('itf.edit') }}
                    </VBtn>
                    <VBtn
                      type="button"
                      size="small"
                      variant="tonal"
                      color="error"
                      @click.stop="removeStatusSubscription(index)"
                    >
                      <VIcons type="delete" />
                      {{ $t('itf.delete') }}
                    </VBtn>
                  </div>
                </td>
              </tr>
            </tbody>
          </VTable>
        </div>
      </section>

      <!-- Permissions by type -->
      <section class="role-edit-v2__card">
        <div class="role-edit-v2__card-header">
          <VIcons type="admin_panel_settings" color="primary" />
          <h2 class="role-edit-v2__card-title">
            {{ $t('itf.type_permissions') || 'Права доступа по типам' }}
          </h2>
        </div>
        <div class="role-edit-v2__card-body">
          <div class="role-edit-v2__perm-list">
            <div class="role-edit-v2__perm-card">
              <div class="role-edit-v2__perm-icon">
                <VIcons type="menu_book" />
              </div>
              <div class="role-edit-v2__perm-body">
                <h3 class="role-edit-v2__perm-title">Права доступа к справочникам</h3>
                <p class="role-edit-v2__perm-desc">
                  Страницы администрирования справочников и конфигурации паспортов
                </p>
              </div>
              <VBtn variant="tonal" @click="showTypePermissionsModal = true">
                <VIcons type="settings" />
                {{ $t('itf.configure') || 'Настроить' }}
              </VBtn>
            </div>

            <div class="role-edit-v2__perm-card">
              <div class="role-edit-v2__perm-icon">
                <VIcons type="map" />
              </div>
              <div class="role-edit-v2__perm-body">
                <h3 class="role-edit-v2__perm-title">Права доступа к элементам</h3>
                <p class="role-edit-v2__perm-desc">Элементы на карте и в списках инвентаризации</p>
              </div>
              <VBtn variant="tonal" @click="showElementPermissionsModal = true">
                <VIcons type="settings" />
                {{ $t('itf.configure') || 'Настроить' }}
              </VBtn>
            </div>

            <div class="role-edit-v2__perm-card">
              <div class="role-edit-v2__perm-icon">
                <VIcons type="view_list" />
              </div>
              <div class="role-edit-v2__perm-body">
                <h3 class="role-edit-v2__perm-title">{{ trs('itf.passport_detailed_view_permission') }}</h3>
                <p class="role-edit-v2__perm-desc">{{ trs('itf.passport_detailed_view_permission_desc') }}</p>
              </div>
              <VBtn variant="tonal" @click="showPassportDetailedViewPermissionsModal = true">
                <VIcons type="settings" />
                {{ $t('itf.configure') || 'Настроить' }}
              </VBtn>
            </div>
          </div>
        </div>
      </section>

      <!-- Footer -->
      <div class="role-edit-v2__footer">
        <VBtn type="submit" :loading="isSaved" :disabled="disabled">
          <VIcons type="save" />
          {{ $t('itf.save') }}
        </VBtn>

        <VBtn :to="backLink" color="default" variant="outlined">
          <VIcons type="arrow_back" />
          {{ $t('itf.back_list') }}
        </VBtn>
      </div>
    </div>

    <ObjectTypePermissionsModal
      v-model:show="showTypePermissionsModal"
      v-model="model.data.type_permissions"
      :group-id="roleGroupId"
    />

    <ElementPermissionsModal
      v-model:show="showElementPermissionsModal"
      v-model="model.data.element_permissions"
      :group-id="roleGroupId"
    />

    <PassportDetailedViewPermissionsModal
      v-model:show="showPassportDetailedViewPermissionsModal"
      v-model="model.data.passport_permissions"
      :group-id="roleGroupId"
    />
  </VForm>
</template>

<style scoped lang="scss">
.role-edit-v2 {
  min-height: calc(100vh - 88px);
  padding: 16px;
  background: #f7f7f7;
}

.role-edit-v2__hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
}

.role-edit-v2__back-btn {
  flex-shrink: 0;
  margin-top: 0;
}

.role-edit-v2__hero-copy {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.role-edit-v2__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.role-edit-v2__title {
  margin: 0;
  font-size: clamp(22px, 2.4vw, 30px);
  line-height: 1.15;
  font-weight: 400;
}

.role-edit-v2__subtitle {
  margin: 0;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.35;
}

.role-edit-v2__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
  min-width: 280px;
}

.role-edit-v2__controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
}

.role-edit-v2__body {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.role-edit-v2__card {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #fff;
  overflow: hidden;
}

.role-edit-v2__card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
}

.role-edit-v2__card-title {
  font-size: 16px;
  font-weight: 500;
  line-height: 1.3;
  margin: 0;
}

.role-edit-v2__card-body {
  padding: 16px 18px 18px;
}

.role-edit-v2__form-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 16px 12px;
  align-items: start;

  > * {
    grid-column: span 4;
  }

  @media (max-width: 1100px) {
    > * {
      grid-column: span 6;
    }
  }

  @media (max-width: 720px) {
    > * {
      grid-column: span 12;
    }
  }
}

.role-edit-v2__checkbox-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.role-edit-v2__subform {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
  margin-bottom: 16px;
}

.role-edit-v2__subform-field {
  flex: 1;
  min-width: 200px;
}

.role-edit-v2__subform-switch {
  flex-shrink: 0;
  margin-bottom: 4px;
}

.role-edit-v2__subform-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.role-edit-v2__perm-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.role-edit-v2__perm-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
  transition:
    box-shadow 0.15s,
    border-color 0.15s;

  &:hover {
    border-color: #bdbdbd;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
}

.role-edit-v2__perm-icon {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(25, 118, 210, 0.06);
  color: #1976d2;
  flex-shrink: 0;
}

.role-edit-v2__perm-body {
  flex: 1;
  min-width: 0;
}

.role-edit-v2__perm-title {
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
  margin: 0;
}

.role-edit-v2__perm-desc {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 2px;
  margin-bottom: 0;
}

.role-edit-v2__footer {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding: 0 2px;
  flex-wrap: wrap;
}

@media (max-width: 960px) {
  .role-edit-v2__hero {
    flex-direction: column;
    align-items: stretch;
  }

  .role-edit-v2__actions {
    width: 100%;
    align-items: stretch;
    min-width: 0;
  }

  .role-edit-v2__controls {
    justify-content: flex-start;
  }
}
</style>
